import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core'
import { StreetGeoJson } from '@containers/map/map.interface'
import { getStreetGeoJsonState } from '@containers/map/map.selectors'
import { SetDefaultData } from '@containers/map/map.actions'
import { getSettingsState } from '@containers/settings/settings.selectors'
import { Store, select } from '@ngrx/store'
import Feature from 'ol/Feature'
import Map from 'ol/Map'
import Overlay from 'ol/Overlay'
import View from 'ol/View'
import * as Extent from 'ol/extent'
import GeoJSON from 'ol/format/GeoJSON'
import MultiLineString from 'ol/geom/MultiLineString'
import { Draw, Modify, Snap } from 'ol/interaction'
import {
  Image as ImageLayer,
  Tile as TileLayer,
  Vector as VectorLayer,
} from 'ol/layer'
import { transform } from 'ol/proj'
import { ImageWMS, TileWMS, Vector as VectorSource } from 'ol/source'
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from 'ol/style'
import { Subscription } from 'rxjs'
import { environment } from '@env/environment'
import { Geometry } from 'ol/geom'
// import { environment } from '../../../environments/environment';
import { bbox } from 'ol/loadingstrategy.js'
import Control from 'ol/control/Control'
import Bar from 'ol-ext/control/Bar'
import LayerPopup from 'ol-ext/control/LayerPopup'
import { FetchActiveLayersRequest } from '@shared/shared.actions'
import { getActiveLayers } from '@shared/shared.select'

@Component({
  selector: 'preview-map',
  templateUrl: './preview-map.component.html',
  styleUrls: ['./preview-map.component.css'],
})
export class PreviewMapComponent implements OnInit, OnDestroy, AfterViewInit {
  public map: Map
  public draw: Draw
  public _polygonFeature: Draw
  public vector: VectorLayer<any>
  public streetsLayer: VectorLayer<any>
  public snap: Snap
  public overlay: Overlay
  public isPopupVisible: boolean
  public raster: ImageLayer<any>
  public pointFeature: Feature
  public modify: Modify
  public position: number[] = []
  public settings: any
  private settings$: Subscription
  private streetGeoJson$: Subscription
  private streetGeoJson: StreetGeoJson
  private streetLineFeature: Feature
  public popupData: any = {}
  public extent: any
  public resolution: any
  public isCoordinatesPopupVisible = false
  public mainbar: Bar
  private layerPopup: LayerPopup
  private activeLayersState$: Subscription
  public activeLayers = []
  private interval: any
  public selectedFeatureType: string = 'property'
  public featureTypeOptions = [
    { label: 'PROPERTIES', value: 'property' },
    { label: 'LICENSES', value: 'license' },
  ]

  @ViewChild('olPopup', { static: false }) popup: ElementRef

  constructor(public store: Store<any>) {}

  ngOnInit() {
    this.activeLayersState$ = this.store
      .pipe(select(getActiveLayers))
      .subscribe((state) => {
        if (this.activeLayers.length === 0) {
          this.activeLayers = state.toJS()
          this.interval = setInterval(() => {
            if (this.map) {
              this.addLayersToMap()

              clearInterval(this.interval)
            }
          })
        }
      })

    this.store.dispatch<any>(new FetchActiveLayersRequest())
  }

  ngAfterViewInit() {
    this.store.dispatch<any>(new SetDefaultData('streetGeoJson'))

    this.settings$ = this.store
      .pipe(select(getSettingsState))
      .subscribe((state) => {
        this.settings = state.get('data')
        if (this.settings.size && !this.map) {
          this.initMap()
        }
      })

    this.streetGeoJson$ = this.store
      .pipe(select(getStreetGeoJsonState))
      .subscribe((state) => {
        if (this.streetGeoJson !== state.get('data').toJS()) {
          this.streetGeoJson = state.get('data').toJS()

          if (this.streetGeoJson.coordinates) {
            this.markStreet(this.streetGeoJson.coordinates)
          }
        }
      })
  }

  ngOnDestroy() {
    this.settings$.unsubscribe()
    this.streetGeoJson$.unsubscribe()

    if (this.activeLayersState$) {
      this.activeLayersState$.unsubscribe()
    }

    this.clearMap()
  }

  markStreet(coordinates: Array<[]>) {
    if (this.streetLineFeature) {
      const layer = this.map.getLayers().getArray()[2] as any
      layer.getSource().removeFeature(this.streetLineFeature)
    }

    this.streetLineFeature = new Feature({
      geometry: new MultiLineString(coordinates),
      // .transform(
      //   'EPSG:4326',
      //   'EPSG:3857',
      // ),
      name: 'street',
    })

    this.streetLineFeature.setStyle(
      new Style({
        stroke: new Stroke({
          color: 'yellow',
          width: 4,
        }),
      }),
    )

    const layer = this.map.getLayers().getArray()[2] as any
    layer.getSource().addFeature(this.streetLineFeature)
    this.map.getView().fit(this.streetLineFeature.getGeometry().getExtent())
  }

  clearMap() {
    this.streetLineFeature = null

    this.map.getLayers().clear()

    // Remove all overlays (e.g., popups, markers)
    this.map.getOverlays().clear()

    // Remove all interactions (e.g., draw, select)
    this.map.getInteractions().clear()

    this.map = null
  }

  addControls() {
    this.addCoordinatesFinderButton()
  }

  setOpacity = (hex: string, alpha: number) =>
    hex
      ? `${hex}${Math.floor(alpha * 255)
          .toString(16)
          .padStart(2)}`
      : null

  addLayersToMap() {
    const _this = this

    if (!!this.layerPopup) return

    if (!this.layerPopup && this.activeLayers.length) {
      this.activeLayers.forEach(({ id, color, name }) => {
        const source = new VectorSource({
          format: new GeoJSON(),
          url: `${environment.API_ENDPOINT}/gis/public/layer/${id}`,
        })

        const styles = {
          MultiPolygon: new Style({
            stroke: new Stroke({
              color: _this.setOpacity(color, 0.5),
              width: 1,
            }),
            fill: new Fill({
              color: _this.setOpacity(color, 0.3),
            }),
          }),
        }

        const vector = new VectorLayer({
          properties: {
            layerId: id,
            name,
          },
          visible: false,
          source,
          style: function (feature: any) {
            return styles[feature.getGeometry().getType()]
          },
        })

        _this.map.addLayer(vector)
      })

      this.layerPopup = new LayerPopup()

      _this.map.addControl(this.layerPopup)
    }
  }

  initMap() {
    const {
      GIS_GEOSERVER_URL,
      GIS_GEOSERVER_WORKSPACE,
      GIS_BASE_LAYER,
      GIS_MAP_EXTENT,
    } = this.settings.toJS().gis
    this.overlay = new Overlay({
      element: this.popup.nativeElement,
      offset: [-128, 20],
      autoPan: true,
    })

    const extent = GIS_MAP_EXTENT.split(',').map((c: string) => parseFloat(c))
    const origin = extent[0] + ',' + extent[1]

    // this.raster = new TileLayer({
    //   source: new TileWMS({
    this.raster = new ImageLayer({
      source: new ImageWMS({
        url: `${GIS_GEOSERVER_URL}/${GIS_GEOSERVER_WORKSPACE}/wms`,
        params: {
          FORMAT: 'image/jpeg',
          VERSION: '1.1.1',
          tiled: true,
          LAYERS: `${GIS_GEOSERVER_WORKSPACE}:${GIS_BASE_LAYER}`,
          exceptions: 'application/vnd.ogc.se_inimage',
          tilesOrigin: origin,
        },
      }),
    })

    this.streetsLayer = new VectorLayer({
      zIndex: 100,
      source: new VectorSource({
        features: [],
      }),
    })

    const source = this.createFeatureSource()

    this.vector = new VectorLayer({
      zIndex: 101,
      source,
      style: function (feature: any) {
        let color: string

        switch (feature.values_.assessibility) {
          case 'ASSESSABLE':
            color = '#3273dc'
            break

          case 'NOT_ASSESSABLE':
            color = 'red'
            break

          case 'TO_BE_ASSESSED':
            color = 'orange'
            break

          default:
            break
        }

        // If license_set exists, show circle with number inside
        if (
          feature.values_.license_set &&
          feature.values_.license_set.length > 0
        ) {
          return new Style({
            image: new CircleStyle({
              radius: 8,
              fill: new Fill({ color: '#3273dc' }),
              stroke: new Stroke({ color: '#fff', width: 1 }),
            }),
            text: new Text({
              text: feature.values_.license_set.length.toString(),
              font: 'bold 9px sans-serif',
              fill: new Fill({ color: '#fff' }),
            }),
          })
        }

        // Default style for features without license_set
        return new Style({
          image: new CircleStyle({
            radius: 5,
            fill: new Fill({ color }),
            stroke: new Stroke({ color: '#fff', width: 0.5 }),
          }),
        })
      },
    })

    this.map = new Map({
      layers: [this.raster, this.vector, this.streetsLayer],
      target: 'ol-map',
      overlays: [this.overlay],
      view: new View({
        projection: 'EPSG:4326',
        center: this.position.length
          ? this.position
          : [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2],
        zoom: 15,
      }),
    })

    this.map.on('click', ({ pixel }) => {
      const feature = this.map.forEachFeatureAtPixel(pixel, function (feature) {
        return feature
      }) as any

      if (feature) {
        const geometry = feature.getGeometry() as any
        const coordinates = geometry.getCoordinates()
        console.log(feature)
        const { id, property_code, license_set } = feature.values_
        this.popupData = { id, property_code, licenses: license_set }
        this.isPopupVisible = true
        this.overlay.setPosition(coordinates)
      } else {
        this.isPopupVisible = false
      }
    })

    this.addControls()
  }

  createFeatureSource(): VectorSource {
    const _this = this
    const source = new VectorSource({
      format: new GeoJSON(),
      loader: function (extent, resolution, projection, success, failure) {
        const proj = projection.getCode()
        const endpoint =
          _this.selectedFeatureType === 'property'
            ? 'getPropertyCoordinateFeatures'
            : 'getLicenseCoordinateFeatures'
        const url = `${
          environment.API_ENDPOINT
        }/gis/public/${endpoint}?bbox=${extent.join(
          ',',
        )},${proj}&maxFeatures=500`
        const xhr = new XMLHttpRequest()
        xhr.open('GET', url)
        const onError = function () {
          source.removeLoadedExtent(extent)
          failure()
        }
        xhr.onerror = onError
        xhr.onload = function () {
          if (xhr.status == 200 && xhr.responseText) {
            const features = source
              .getFormat()
              .readFeatures(xhr.responseText) as Feature<Geometry>[]

            source.clear()
            source.addFeatures(features)

            success(features)
          } else {
            onError()
          }
        }
        xhr.send()
      },
      strategy: function (extent, resolution) {
        if (
          this.resolution != resolution ||
          JSON.stringify(extent) !== JSON.stringify(this.extent)
        ) {
          this.loadedExtentsRtree_.clear()
          this.extent = extent
          this.resolution = resolution
        }
        return [extent]
      },
    })
    return source
  }

  onFeatureTypeChange(value: string): void {
    this.selectedFeatureType = value
    if (this.vector) {
      const newSource = this.createFeatureSource()
      this.vector.setSource(newSource)
    }
  }

  addCoordinatesFinderButton(): void {
    const button = document.createElement('button')
    const icon = document.createElement('img')
    icon.setAttribute('src', '/assets/search-icon.svg')
    button.appendChild(icon)
    button.addEventListener('click', () => {
      this.isCoordinatesPopupVisible = !this.isCoordinatesPopupVisible
    })
    button.className = 'coordinates-button'

    const controlDiv = document.createElement('div')
    controlDiv.className = 'coordinates-button-control'
    controlDiv.appendChild(button)

    const controlElement = document.createElement('div')
    controlElement.className = 'ol-unselectable ol-control coordinates-control'
    controlElement.appendChild(controlDiv)

    const mapControls = this.map.getControls()
    mapControls.push(
      new (class extends Control {
        constructor() {
          super({
            element: controlElement,
          })
        }
      })(),
    )
  }
}
